import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, ArrowRight, ClipboardList, Save,
  Plus, Trash2, Clock, FileText,
  Check, Search, Download, Lock, X, RefreshCw,
  Activity, Heart, Droplets, Weight, Thermometer,
  Building2, User, ZoomIn, RotateCw, Sun, Move,
  Play, Ruler, Monitor, Crosshair, Maximize2,
} from 'lucide-react';
import PatientPanelHeader from '@/components/PatientPanelHeader';
import { toast } from 'sonner';

/* ═══ Constants ═══ */
const SETTINGS = ['Bassa Complessita', 'Media Complessita', 'Alta Complessita', 'Dimissione Concordata'];
const CM_LIST = ['Inf. Marco Rossi - Distretto 1', 'Inf. Giulia Conti - Distretto 2', 'Dr.ssa Anna Lombardi - Distretto 3', 'Dr. Paolo Moretti - Distretto 4'];
const CATEGORIE_AREA = ['Rischio cadute', 'Lesioni da decubito', 'Dolore cronico', 'Malnutrizione', 'Deficit cognitivo', 'Incontinenza', 'Isolamento sociale', 'Disfagia', 'Rischio infettivo'];
const FIGURE_PROF = ['Infermiere', 'Fisioterapista', 'Logopedista', 'OSS', 'Psicologo', 'Assistente Sociale', 'Medico Specialista'];
const PRESTAZIONI_TIPO = ['Medicazione semplice', 'Medicazione complessa', 'Prelievo ematico', 'Terapia iniettiva', 'Gestione catetere', 'Riabilitazione motoria', 'Riabilitazione respiratoria', 'Trattamento logopedico', 'Igiene e cura persona', 'Supporto psicologico', 'Educazione terapeutica', 'Monitoraggio parametri vitali'];
const FREQUENZE = ['1 accesso/settimana', '2 accessi/settimana', '3 accessi/settimana', '5 accessi/settimana', 'Giornaliera', 'Al bisogno'];
const TELE_PARAMS = [
  { key: 'pressione', label: 'Pressione', icon: Heart, defaultMin: 90, defaultMax: 140, unit: 'mmHg' },
  { key: 'peso', label: 'Peso', icon: Weight, defaultMin: 40, defaultMax: 120, unit: 'kg' },
  { key: 'spo2', label: 'SpO2', icon: Droplets, defaultMin: 88, defaultMax: 100, unit: '%' },
  { key: 'glicemia', label: 'Glicemia', icon: Activity, defaultMin: 70, defaultMax: 180, unit: 'mg/dL' },
];

const WIZARD_STEPS = [
  { num: 1, label: 'Inquadramento' },
  { num: 2, label: 'Obiettivi' },
  { num: 3, label: 'Interventi' },
  { num: 4, label: 'Telemedicina' },
  { num: 5, label: 'Riepilogo e Firma' },
];

const MOCK_EXAMS = [
  { id: 1, title: 'TAC torace con MDC', date: '22/09/2025', time: '10:50', facility: 'Radiologia | Careggi Firenze', doctor: 'Dott. Mario Rossi', type: 'Referto', medicalArea: 'Radiologia', month: 'Settembre 2025' },
  { id: 2, title: 'Esami di laboratorio', date: '18/09/2025', time: '08:30', facility: 'Laboratorio Analisi | ASL Roma 1', doctor: 'Dott.ssa Elena Bianchi', type: 'Esame', medicalArea: 'Laboratorio', month: 'Settembre 2025' },
  { id: 3, title: 'Ricovero breve osservazione', date: '10/09/2025', time: '14:15', facility: 'Cardiologia | Policlinico Gemelli', doctor: 'Dott. Andrea Conti', type: 'Episodio', medicalArea: 'Cardiologia', month: 'Settembre 2025' },
  { id: 4, title: 'Ecografia addominale', date: '28/08/2025', time: '09:00', facility: 'Diagnostica | S. Camillo Roma', doctor: 'Dott.ssa Laura Ferri', type: 'Referto', medicalArea: 'Diagnostica', month: 'Agosto 2025' },
  { id: 5, title: 'RX rachide lombare', date: '15/08/2025', time: '11:20', facility: 'Radiologia | Careggi Firenze', doctor: 'Dott. Mario Rossi', type: 'Referto', medicalArea: 'Radiologia', month: 'Agosto 2025' },
  { id: 6, title: 'Spirometria globale', date: '02/08/2025', time: '16:45', facility: 'Pneumologia | ASL Roma 1', doctor: 'Dott. Giovanni Neri', type: 'Esame', medicalArea: 'Pneumologia', month: 'Agosto 2025' },
  { id: 7, title: 'Visita neurologica', date: '20/07/2025', time: '10:00', facility: 'Neurologia | Policlinico Gemelli', doctor: 'Dott.ssa Sara Lombardi', type: 'Referto', medicalArea: 'Neurologia', month: 'Luglio 2025' },
  { id: 8, title: 'Emocromo completo', date: '05/07/2025', time: '07:45', facility: 'Laboratorio Analisi | ASL Roma 1', doctor: 'Dott.ssa Elena Bianchi', type: 'Esame', medicalArea: 'Laboratorio', month: 'Luglio 2025' },
];

const MEDICAL_AREAS = [...new Set(MOCK_EXAMS.map(e => e.medicalArea))];
const TYPE_BORDER_COLOR = { Episodio: 'border-l-cyan-500', Referto: 'border-l-purple-500', Esame: 'border-l-emerald-500' };

/* ═══ Exam Detail Modal ═══ */
function ExamDetailModal({ exam, onClose, patient }) {
  const [activeTool, setActiveTool] = useState('zoom');
  const [activePreset, setActivePreset] = useState('lung');

  if (!exam) return null;

  const isRadiologico = /TAC|RX|Radiografia|TC /i.test(exam.title);
  const isEsame = exam.type === 'Esame';

  const renderRefertoEsame = () => (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" data-testid="exam-preview-referto">
      {/* Finto header ASL */}
      <div className="shrink-0 bg-blue-50 dark:bg-blue-950/40 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 dark:bg-blue-500 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
          <div>
            <p className="text-xs font-bold text-blue-900 dark:text-blue-200 uppercase tracking-wider">Servizio Sanitario Nazionale</p>
            <p className="text-[10px] text-blue-700 dark:text-blue-400">{exam.facility}</p>
          </div>
        </div>
        <Badge className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-0 text-[10px] font-bold">{isEsame ? 'ESITO ESAME' : 'REFERTO'}</Badge>
      </div>
      {/* Corpo referto simulato */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 text-sm max-h-[440px]">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Tipologia</p>
            <p className="text-base font-bold text-foreground">{exam.title}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Data Refertazione</p>
            <p className="text-sm font-semibold text-foreground">{exam.date} — {exam.time}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Medico Refertante</p>
          <p className="text-sm text-foreground">{exam.doctor} — {exam.medicalArea}</p>
        </div>

        {/* Immagine radiologica per TAC / RX */}
        {isRadiologico && (
          <div data-testid="exam-radio-image">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Anteprima Diagnostica</p>
            <img
              src="https://images.unsplash.com/photo-1616012480717-fd9867059ca0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIweHJheSUyMGNoZXN0JTIwc2NhbiUyMHJhZGlvZ3JhcGh8ZW58MHx8fHwxNzc1ODE4MjYwfDA&ixlib=rb-4.1.0&q=85&w=800"
              className="w-full h-64 object-cover rounded-lg grayscale"
              alt="Anteprima RX"
            />
          </div>
        )}

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Quesito Clinico</p>
          <p className="text-sm text-muted-foreground italic">Valutazione strumentale per follow-up diagnostico nel contesto di percorso ADI.</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descrizione / Risultati</p>
          <div className="bg-slate-50 dark:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-2">
            {isRadiologico ? (
              <>
                <p className="text-sm text-foreground leading-relaxed">L'indagine evidenzia un quadro compatibile con le condizioni cliniche note. Non si rilevano significative variazioni rispetto ai precedenti controlli.</p>
                <p className="text-sm text-foreground leading-relaxed">Parenchima nei limiti. Nessuna lesione focale di recente insorgenza. Regolari i rapporti anatomici delle strutture esaminate.</p>
              </>
            ) : isEsame ? null : (
              <>
                <p className="text-sm text-foreground leading-relaxed">L'indagine evidenzia un quadro compatibile con le condizioni cliniche note. Non si rilevano significative variazioni rispetto ai precedenti controlli.</p>
                <p className="text-sm text-foreground leading-relaxed">Parenchima nei limiti. Nessuna lesione focale di recente insorgenza. Regolari i rapporti anatomici delle strutture esaminate.</p>
                <p className="text-sm text-foreground leading-relaxed">Eventuali reperti meritevoli di approfondimento sono stati segnalati nella sezione conclusioni.</p>
              </>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Conclusioni</p>
          <p className="text-sm text-foreground">Quadro sostanzialmente stazionario. Si consiglia rivalutazione a 3 mesi dal presente esame o anticipata in caso di variazioni cliniche significative.</p>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-dashed border-slate-300 dark:border-slate-600">
          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
          <p className="text-[10px] text-muted-foreground">Documento firmato digitalmente — {exam.doctor}</p>
        </div>
      </div>
    </div>
  );

  /* PDF Viewer simulato per Esami di Laboratorio */
  const renderEsamePDF = () => (
    <div className="bg-slate-800 dark:bg-slate-900 rounded-xl overflow-hidden h-[500px] flex flex-col" data-testid="exam-preview-pdf">
      {/* Finta barra PDF viewer */}
      <div className="shrink-0 bg-slate-700 dark:bg-slate-800 px-4 py-2.5 flex items-center justify-between border-b border-slate-600">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-300">referto_esami_{exam.date?.replace(/\//g, '')}.pdf</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400">1 / 1</span>
          <span className="text-[10px] text-slate-500 mx-1">|</span>
          <span className="text-[10px] text-slate-400">100%</span>
        </div>
      </div>
      {/* Foglio bianco simulato */}
      <div className="flex-1 overflow-y-auto p-6 flex justify-center">
        <div className="bg-white max-w-2xl w-full shadow-2xl aspect-[1/1.4] rounded-sm p-8 flex flex-col" data-testid="exam-pdf-sheet">
          {/* Intestazione referto */}
          <div className="border-b-2 border-blue-700 pb-3 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] font-bold text-blue-900 uppercase tracking-[0.15em]">Servizio Sanitario Nazionale</p>
                <p className="text-[8px] text-slate-500">{exam.facility}</p>
              </div>
              <p className="text-[8px] text-slate-500 text-right">Prot. n. {Math.floor(Math.random() * 90000) + 10000}/LAB</p>
            </div>
          </div>
          <p className="text-center text-[10px] font-bold text-slate-800 uppercase tracking-wider mb-3">Referto Esami di Laboratorio</p>
          <div className="flex justify-between text-[8px] text-slate-600 mb-4">
            <span>Paziente: <b className="text-slate-800">[Dati anagrafici]</b></span>
            <span>Data prelievo: <b className="text-slate-800">{exam.date}</b></span>
          </div>
          {/* Tabella risultati simulata */}
          <table className="w-full text-[8px] border-collapse mb-4">
            <thead>
              <tr className="border-b border-slate-300">
                <th className="text-left py-1 text-slate-600 font-semibold">Esame</th>
                <th className="text-center py-1 text-slate-600 font-semibold">Risultato</th>
                <th className="text-center py-1 text-slate-600 font-semibold">Unita</th>
                <th className="text-right py-1 text-slate-600 font-semibold">Rif.</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              {[
                ['Emoglobina', '12.4', 'g/dL', '12.0 - 16.0'],
                ['Leucociti', '7.200', '/mcL', '4.000 - 10.000'],
                ['Piastrine', '245.000', '/mcL', '150.000 - 400.000'],
                ['Creatinina', '0.98', 'mg/dL', '0.60 - 1.20'],
                ['Glicemia', '102', 'mg/dL', '70 - 110'],
                ['Colesterolo Tot.', '198', 'mg/dL', '< 200'],
                ['Trigliceridi', '145', 'mg/dL', '< 150'],
                ['AST (GOT)', '22', 'U/L', '10 - 40'],
                ['ALT (GPT)', '28', 'U/L', '10 - 45'],
                ['PCR', '0.5', 'mg/L', '< 5.0'],
                ['VES', '12', 'mm/h', '< 20'],
                ['Azotemia', '35', 'mg/dL', '15 - 50'],
              ].map(([name, val, unit, ref], i) => (
                <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                  <td className="py-1 font-medium">{name}</td>
                  <td className="py-1 text-center font-bold">{val}</td>
                  <td className="py-1 text-center text-slate-500">{unit}</td>
                  <td className="py-1 text-right text-slate-500">{ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-auto pt-3 border-t border-slate-200">
            <p className="text-[7px] text-slate-500 italic">Valori nella norma. Nessuna alterazione significativa. Documento firmato digitalmente.</p>
            <p className="text-[7px] text-slate-500 mt-1">Refertante: {exam.doctor} — {exam.facility}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEpisodio = () => (
    <div className="bg-white dark:bg-slate-800 h-[500px] rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" data-testid="exam-preview-episodio">
      {/* Finto header Ospedale */}
      <div className="shrink-0 bg-cyan-50 dark:bg-cyan-950/40 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-600 dark:bg-cyan-500 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
          <div>
            <p className="text-xs font-bold text-cyan-900 dark:text-cyan-200 uppercase tracking-wider">Foglio di Dimissione</p>
            <p className="text-[10px] text-cyan-700 dark:text-cyan-400">{exam.facility}</p>
          </div>
        </div>
        <Badge className="bg-cyan-100 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-300 border-0 text-[10px] font-bold">EPISODIO</Badge>
      </div>
      {/* Corpo dimissione simulato */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm">
        <div className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Data Accesso</p>
            <p className="text-sm font-semibold text-foreground">{exam.date} — {exam.time}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Data Dimissione</p>
            <p className="text-sm font-semibold text-foreground">{exam.date}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Reparto</p>
            <p className="text-sm text-foreground">{exam.medicalArea}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Medico Responsabile</p>
            <p className="text-sm text-foreground">{exam.doctor}</p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Motivo Accesso</p>
          <div className="bg-slate-50 dark:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-sm text-foreground">Accesso in regime di urgenza per sospetto evento acuto. Paziente giunto in condizioni emodinamicamente stabili, sottoposto ad accertamenti strumentali e laboratoristici.</p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Diagnosi alla Dimissione</p>
          <div className="bg-slate-50 dark:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-sm font-semibold text-foreground">Episodio acuto in soggetto con comorbidita multiple nota (ICD-9: 428.0)</p>
            <p className="text-xs text-muted-foreground mt-1">Diagnosi secondarie: Ipertensione arteriosa, Diabete mellito tipo 2</p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Terapia alla Dimissione</p>
          <div className="bg-slate-50 dark:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-1.5">
            <p className="text-sm text-foreground">1. Furosemide 25mg — 1 cp ore 8:00</p>
            <p className="text-sm text-foreground">2. Ramipril 5mg — 1 cp ore 8:00</p>
            <p className="text-sm text-foreground">3. Metformina 500mg — 1 cp ore 12:00 e 20:00</p>
            <p className="text-sm text-foreground">4. Pantoprazolo 20mg — 1 cp ore 8:00 a digiuno</p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Indicazioni al Follow-up</p>
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800/40 p-3">
            <p className="text-sm text-foreground">Controllo cardiologico entro 15 gg. Esami ematochimici (emocromo, funz. renale, elettroliti) entro 7 gg. Dieta iposodica. Monitoraggio peso corporeo giornaliero.</p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-dashed border-slate-300 dark:border-slate-600">
          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
          <p className="text-[10px] text-muted-foreground">Documento firmato digitalmente — {exam.doctor}</p>
        </div>
      </div>
    </div>
  );

  /* ═══ DICOM Viewer — Full-screen radiological viewer ═══ */
  if (isRadiologico) {
    const DICOM_TOOLS = [
      { id: 'zoom', label: 'Zoom', icon: ZoomIn },
      { id: 'pan', label: 'Pan', icon: Move },
      { id: 'window', label: 'Window', icon: Sun },
      { id: 'cine', label: 'Cine', icon: Play },
      { id: 'measure', label: 'Misura', icon: Ruler },
    ];
    const PRESETS = [
      { id: 'lung', label: 'Lung', ww: 1500, wl: -600 },
      { id: 'mediastinum', label: 'Medias.', ww: 350, wl: 50 },
      { id: 'bone', label: 'Bone', ww: 1800, wl: 400 },
    ];
    const pName = patient ? `${patient.cognome} ${patient.nome}` : 'N/D';
    const pAge = patient ? `${patient.eta} anni` : '';
    const pCF = patient?.codiceFiscale || 'N/D';
    const cp = PRESETS.find(p => p.id === activePreset) || PRESETS[0];

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8" onClick={onClose} data-testid="dicom-viewer-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.25 }}
          className="relative w-full max-w-[95vw] lg:max-w-7xl h-[90vh] bg-[#0a0f18] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-800"
          onClick={e => e.stopPropagation()}
          data-testid="dicom-viewer"
        >
          {/* ── HEADER TOOLBAR ── */}
          <div className="shrink-0 h-12 bg-slate-900 border-b border-slate-700/50 flex items-center justify-between px-4" data-testid="dicom-header">
            <div className="flex items-center gap-3 min-w-0">
              <Monitor className="w-4 h-4 text-cyan-400 shrink-0" />
              <span className="text-sm font-bold text-slate-100 tracking-wide truncate">{exam.title}</span>
              <span className="text-slate-600 hidden sm:inline">|</span>
              <span className="text-xs text-slate-400 hidden sm:inline truncate">{exam.date} · {exam.doctor}</span>
            </div>
            <div className="flex items-center gap-1">
              {[
                { icon: ZoomIn, tip: 'Ingrandisci' },
                { icon: Sun, tip: 'Contrasto' },
                { icon: RotateCw, tip: 'Ruota' },
                { icon: Ruler, tip: 'Misura' },
              ].map((btn, i) => (
                <button key={i} className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-cyan-300 hover:bg-slate-800 transition-colors" title={btn.tip} data-testid={`dicom-hdr-${btn.tip.toLowerCase()}`}>
                  <btn.icon className="w-4 h-4" />
                </button>
              ))}
              <div className="w-px h-5 bg-slate-700 mx-1.5" />
              <button className="flex items-center gap-1.5 px-3 h-8 rounded bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-300 hover:text-cyan-300 transition-colors" data-testid="dicom-download-btn">
                <Download className="w-3.5 h-3.5" /> Scarica DICOM
              </button>
              <button onClick={onClose} className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-900/50 transition-colors ml-1" data-testid="dicom-close-btn">
                <X className="w-4.5 h-4.5" />
              </button>
            </div>
          </div>

          {/* ── BODY: Side Panel + Multi-Pane ── */}
          <div className="flex-1 flex min-h-0">
            {/* Side Panel — Tools + Presets */}
            <div className="shrink-0 w-[68px] bg-slate-900/80 border-r border-slate-800/60 flex flex-col items-center py-3 gap-1" data-testid="dicom-side-panel">
              {DICOM_TOOLS.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all
                    ${activeTool === tool.id
                      ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-inset ring-cyan-500/30'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/70'}`}
                  data-testid={`dicom-tool-${tool.id}`}
                >
                  <tool.icon className="w-[18px] h-[18px]" />
                  <span className="text-[9px] font-medium leading-none">{tool.label}</span>
                </button>
              ))}
              <div className="w-8 h-px bg-slate-700/60 my-2" />
              <p className="text-[7px] text-slate-600 font-bold uppercase tracking-[0.12em] mb-1">Presets</p>
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setActivePreset(p.id)}
                  className={`w-14 h-9 rounded text-[9px] font-semibold transition-all
                    ${activePreset === p.id
                      ? 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/70'}`}
                  data-testid={`dicom-preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Multi-Pane Viewer */}
            <div className="flex-1 flex gap-[2px] bg-slate-950 min-h-0 p-[2px]">
              {/* ── Main Pane (Axial) ── */}
              <div className="flex-1 relative bg-black rounded-sm overflow-hidden flex items-center justify-center" data-testid="dicom-main-pane">
                <img
                  src="https://images.unsplash.com/photo-1616012480717-fd9867059ca0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxDVCUyMHNjYW4lMjBjaGVzdCUyMGF4aWFsJTIwbWVkaWNhbCUyMGltYWdpbmd8ZW58MHx8fHwxNzc1ODI4NjIyfDA&ixlib=rb-4.1.0&q=85"
                  className="max-w-full max-h-full object-contain select-none"
                  alt="DICOM Axial"
                  draggable={false}
                />
                {/* Overlay — Top Left: Patient info */}
                <div className="absolute top-3 left-3 text-[11px] font-mono text-green-400/90 leading-relaxed select-none pointer-events-none" data-testid="dicom-overlay-tl">
                  <p className="font-bold">{pName}</p>
                  <p>{pCF}</p>
                  <p>{pAge} · {exam.date}</p>
                </div>
                {/* Overlay — Top Right: Facility + Slice */}
                <div className="absolute top-3 right-3 text-[11px] font-mono text-green-400/90 text-right leading-relaxed select-none pointer-events-none" data-testid="dicom-overlay-tr">
                  <p>{exam.facility?.split('|')[0]?.trim()}</p>
                  <p>Slice: 45 / 120</p>
                  <p>ST: 2.5mm</p>
                </div>
                {/* Overlay — Bottom Left: Window/Level */}
                <div className="absolute bottom-3 left-3 text-[10px] font-mono text-green-400/70 select-none pointer-events-none">
                  <p>W:{cp.ww} L:{cp.wl}</p>
                  <p>Zoom: 100%</p>
                </div>
                {/* Overlay — Bottom Right: View + Ref */}
                <div className="absolute bottom-3 right-3 text-[10px] font-mono text-green-400/70 text-right select-none pointer-events-none" data-testid="dicom-overlay-br">
                  <p>Axial</p>
                  <p>Ref: {exam.doctor}</p>
                </div>
                {/* Crosshairs */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-cyan-500/15" />
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-cyan-500/15" />
                </div>
                {/* Anatomical markers R/L/A/P */}
                <span className="absolute top-1/2 left-2.5 -translate-y-1/2 text-[10px] font-bold text-cyan-400/50 select-none pointer-events-none">R</span>
                <span className="absolute top-1/2 right-2.5 -translate-y-1/2 text-[10px] font-bold text-cyan-400/50 select-none pointer-events-none">L</span>
                <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold text-cyan-400/50 select-none pointer-events-none">A</span>
                <span className="absolute bottom-7 left-1/2 -translate-x-1/2 text-[10px] font-bold text-cyan-400/50 select-none pointer-events-none">P</span>
              </div>

              {/* ── Right Column: Secondary + 3D ── */}
              <div className="w-64 xl:w-72 flex flex-col gap-[2px] shrink-0">
                {/* Coronal / Sagittal Pane */}
                <div className="flex-1 relative bg-black rounded-sm overflow-hidden flex items-center justify-center" data-testid="dicom-coronal-pane">
                  <img
                    src="https://images.pexels.com/photos/5723878/pexels-photo-5723878.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    className="max-w-full max-h-full object-contain opacity-85 select-none"
                    alt="Coronal View"
                    draggable={false}
                  />
                  <div className="absolute top-2 left-2.5 text-[9px] font-mono select-none pointer-events-none">
                    <p className="font-semibold text-green-400/80">Coronale</p>
                    <p className="text-green-400/50 mt-0.5">Slice: 62 / 120</p>
                  </div>
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-yellow-500/10" />
                    <div className="absolute top-1/2 left-0 right-0 h-px bg-yellow-500/10" />
                  </div>
                  <span className="absolute top-1/2 left-1.5 -translate-y-1/2 text-[8px] font-bold text-yellow-400/40 select-none pointer-events-none">R</span>
                  <span className="absolute top-1/2 right-1.5 -translate-y-1/2 text-[8px] font-bold text-yellow-400/40 select-none pointer-events-none">L</span>
                </div>
                {/* 3D Volume Pane */}
                <div className="flex-1 relative bg-black rounded-sm overflow-hidden flex items-center justify-center" data-testid="dicom-3d-pane">
                  <img
                    src="https://images.unsplash.com/photo-1711409664431-4e7914ac2370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHwzRCUyMHZvbHVtZSUyMHJlbmRlcmluZyUyMENUJTIwc2NhbiUyMGJvZHl8ZW58MHx8fHwxNzc1ODI4NjIzfDA&ixlib=rb-4.1.0&q=85"
                    className="max-w-full max-h-full object-contain opacity-80 select-none"
                    alt="3D Volume Rendering"
                    draggable={false}
                  />
                  <div className="absolute top-2 left-2.5 text-[9px] font-mono select-none pointer-events-none">
                    <p className="font-semibold text-cyan-400/80">3D MIP</p>
                    <p className="text-cyan-400/50 mt-0.5">Volume Rendering</p>
                  </div>
                  <div className="absolute bottom-2 right-2.5 flex items-center gap-1 select-none pointer-events-none">
                    <Crosshair className="w-3 h-3 text-cyan-500/40" />
                    <span className="text-[8px] text-cyan-400/40 font-mono">3D</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── STATUS BAR ── */}
          <div className="shrink-0 h-7 bg-slate-900 border-t border-slate-800/60 flex items-center px-4 gap-4 text-[10px] text-slate-500 font-mono" data-testid="dicom-status-bar">
            <span>W:{cp.ww} L:{cp.wl}</span>
            <span className="text-slate-700">·</span>
            <span>Zoom: 100%</span>
            <span className="text-slate-700">·</span>
            <span>Slice: 45 / 120</span>
            <span className="text-slate-700">·</span>
            <span>ST: 2.5mm</span>
            <span className="text-slate-700">·</span>
            <span className="text-cyan-500/60">{activeTool.charAt(0).toUpperCase() + activeTool.slice(1)}</span>
            <span className="ml-auto text-slate-600">{exam.facility} · PACS v3.2.1</span>
          </div>
        </motion.div>
      </div>
    );
  }

  /* ═══ Standard Modal (Non-radiological) ═══ */
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="exam-modal-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="max-w-4xl w-full mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="exam-modal"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-foreground">{exam.title}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{exam.date}, {exam.time} &middot; {exam.doctor}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="exam-modal-download">
              <Download className="w-3.5 h-3.5" />Scarica PDF
            </Button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="exam-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Modal Body */}
        <div className="p-6">
          {exam.type === 'Episodio' ? renderEpisodio() : isEsame ? renderEsamePDF() : renderRefertoEsame()}
        </div>
      </motion.div>
    </div>
  );
}

const cardCls = 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-5 rounded-lg';
const selectCls = 'w-full h-10 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-sm px-3 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors';
const inputCls = 'w-full h-10 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-sm px-3 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors';
const labelCls = 'text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1.5';

export default function PaiManualCompilation({ patient, onBack, completeEvaluation, cmMode = false, uvmdNotes = null }) {
  const [currentWizardStep, setCurrentWizardStep] = useState(cmMode ? 3 : 1);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  /* ═══ Filtering Logic ═══ */
  const filteredEvents = MOCK_EXAMS.filter(e => {
    if (searchTerm && !e.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (typeFilter !== 'all' && e.type !== typeFilter) return false;
    if (areaFilter !== 'all' && e.medicalArea !== areaFilter) return false;
    if (dateFilter) {
      const [dd, mm, yyyy] = e.date.split('/');
      if (`${yyyy}-${mm}-${dd}` !== dateFilter) return false;
    }
    return true;
  });

  const filteredGroups = filteredEvents.reduce((acc, exam) => {
    const group = acc.find(g => g.month === exam.month);
    if (group) group.exams.push(exam);
    else acc.push({ month: exam.month, exams: [exam] });
    return acc;
  }, []);

  /* ═══ Complex Mode State ═══ */
  const [setting, setSetting] = useState('');
  const [assignedCM, setAssignedCM] = useState('');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [icd9, setIcd9] = useState('');

  const [aree, setAree] = useState([{ categoria: '', obiettivo: '' }]);
  const [interventi, setInterventi] = useState([{ figura: '', prestazione: '', frequenza: '', durata: '' }]);
  const [oreSollievo, setOreSollievo] = useState('');

  const [teleAttivo, setTeleAttivo] = useState(false);
  const [teleParams, setTeleParams] = useState({ pressione: false, peso: false, spo2: false, glicemia: false });
  const [soglie, setSoglie] = useState({});
  const [ausili, setAusili] = useState('');

  const [consensoInformato, setConsensoInformato] = useState(false);
  const [consensoPrivacy, setConsensoPrivacy] = useState(false);
  const [presenzaDAT, setPresenzaDAT] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  /* ═══ Pre-fill UVMD notes in CM mode ═══ */
  const [inquadramentoUVMD] = useState(uvmdNotes?.inquadramento || '');
  const [obiettiviUVMD] = useState(uvmdNotes?.obiettivi || '');

  /* ═══ Helpers ═══ */
  const isMediaAlta = setting === 'Media Complessita' || setting === 'Alta Complessita';

  const addArea = () => setAree(p => [...p, { categoria: '', obiettivo: '' }]);
  const removeArea = (i) => setAree(p => p.filter((_, idx) => idx !== i));
  const updateArea = (i, f, v) => setAree(p => p.map((a, idx) => idx === i ? { ...a, [f]: v } : a));

  const addIntervento = () => setInterventi(p => [...p, { figura: '', prestazione: '', frequenza: '', durata: '' }]);
  const removeIntervento = (i) => setInterventi(p => p.filter((_, idx) => idx !== i));
  const updateIntervento = (i, f, v) => setInterventi(p => p.map((a, idx) => idx === i ? { ...a, [f]: v } : a));

  const toggleTeleParam = (key) => {
    setTeleParams(p => ({ ...p, [key]: !p[key] }));
    if (!teleParams[key]) {
      const def = TELE_PARAMS.find(tp => tp.key === key);
      setSoglie(s => ({ ...s, [key]: { min: def?.defaultMin || 0, max: def?.defaultMax || 100 } }));
    }
  };
  const updateSoglia = (key, field, val) => setSoglie(s => ({ ...s, [key]: { ...s[key], [field]: val } }));

  const importCriticita = () => {
    setAree([
      { categoria: 'Rischio cadute', obiettivo: 'Ridurre il rischio di caduta entro 3 mesi mediante programma riabilitativo e adattamento ambientale.' },
      { categoria: 'Dolore cronico', obiettivo: 'Controllo del dolore con VAS target < 3 attraverso terapia farmacologica e non farmacologica.' },
    ]);
    toast.success('Criticita importate', { description: '2 aree problematiche importate dalla valutazione interRAI.' });
  };

  const canSign = setting && assignedCM && consensoInformato && consensoPrivacy;

  const handleSaveComplex = (draft) => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      if (draft) {
        toast.info('Bozza salvata', { description: 'Il PAI e stato salvato come bozza v1.0.' });
      } else {
        toast.success('PAI approvato e firmato digitalmente', { description: `Setting: ${setting}` });
        if (completeEvaluation) completeEvaluation(patient.id);
        setTimeout(() => onBack(), 1500);
      }
    }, 1800);
  };

  /* ═══ Riepilogo data for Step 5 ═══ */
  const activeInterventCount = interventi.filter(iv => iv.figura || iv.prestazione).length;
  const activeTeleParams = TELE_PARAMS.filter(tp => teleParams[tp.key]);
  const summaryRows = [
    { label: 'Setting', value: setting || '—' },
    { label: 'Case Manager', value: assignedCM || '—' },
    { label: 'Periodo', value: dataInizio && dataFine ? `${dataInizio} — ${dataFine}` : dataInizio || '—' },
    { label: 'Diagnosi ICD-9', value: icd9 || '—' },
    { label: 'Aree problematiche', value: `${aree.filter(a => a.categoria).length} definite` },
    { label: 'Interventi', value: activeInterventCount > 0 ? `${activeInterventCount} configurati` : '—' },
    { label: 'Percorso Sollievo OSS', value: isMediaAlta && oreSollievo ? `${oreSollievo} ore/sett.` : 'Non previsto' },
    { label: 'Telemonitoraggio', value: teleAttivo ? `Attivo (${activeTeleParams.map(p => p.label).join(', ') || 'nessun parametro'})` : 'Non attivo' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="pai-manual-compilation">
      {/* Top bar */}
      <div className="bg-background/95 backdrop-blur-sm border-b border-border px-4 sm:px-6 py-4 shrink-0">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" className="shrink-0" onClick={onBack} data-testid="manual-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-foreground">Compilazione Manuale PAI</h1>
            <p className="text-xs text-muted-foreground">{patient.cognome} {patient.nome} &middot; {patient.eta} anni</p>
          </div>
          <Badge variant="chip" className="text-xs gap-1"><ClipboardList className="w-3 h-3" /> Manuale</Badge>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          <PatientPanelHeader patient={patient} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* ═══ LEFT: Timeline Storico Clinico ═══ */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl h-[750px] overflow-hidden flex flex-col" data-testid="timeline-column">

                {/* ── Sticky Toolbar ── */}
                <div className="shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800 p-4 space-y-3 z-10" data-testid="timeline-toolbar">
                  {/* Row 1: Title + Refresh */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-bold text-foreground">Storico Clinico</h2>
                    <button className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="timeline-refresh">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Row 2: Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Cerca esame, referto o episodio..."
                      className="w-full h-9 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-sm pl-9 pr-3 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                      data-testid="timeline-search"
                    />
                  </div>
                  {/* Row 3: Filters */}
                  <div className="flex gap-2">
                    <select
                      value={typeFilter}
                      onChange={e => setTypeFilter(e.target.value)}
                      className="flex-1 h-9 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-xs px-2.5 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                      data-testid="timeline-filter-type"
                    >
                      <option value="all">Tutte le tipologie</option>
                      <option value="Episodio">Solo Episodi</option>
                      <option value="Referto">Solo Referti</option>
                      <option value="Esame">Solo Esami</option>
                    </select>
                    <select
                      value={areaFilter}
                      onChange={e => setAreaFilter(e.target.value)}
                      className="flex-1 h-9 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-xs px-2.5 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                      data-testid="timeline-filter-area"
                    >
                      <option value="all">Tutte le aree</option>
                      {MEDICAL_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input
                      type="date"
                      value={dateFilter}
                      onChange={e => setDateFilter(e.target.value)}
                      className="w-36 h-9 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-foreground text-xs px-2.5 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                      data-testid="timeline-date-filter"
                    />
                  </div>
                </div>

                {/* ── Scrollable Timeline ── */}
                <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6" data-testid="exam-list">
                  {filteredGroups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Search className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-muted-foreground">Nessun risultato</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Prova a modificare i filtri di ricerca.</p>
                    </div>
                  ) : (
                  <div className="relative pl-6 border-l-2 border-slate-200 dark:border-slate-700 ml-4">
                    {filteredGroups.map((group, gi) => (
                      <div key={group.month} className={gi < filteredGroups.length - 1 ? 'mb-8' : ''}>
                        {/* Month header with dot */}
                        <div className="relative flex items-center mb-4 -ml-6">
                          <div className="absolute -left-[9px] h-4 w-4 rounded-full bg-primary border-4 border-white dark:border-slate-900 z-[1]" />
                          <h3 className="font-bold text-sm text-slate-800 dark:text-white ml-5">{group.month}</h3>
                          <span className="ml-auto text-[10px] text-muted-foreground">{group.exams.length} eventi</span>
                        </div>

                        {/* Exam cards */}
                        <div>
                          {group.exams.map(exam => (
                            <div key={exam.id} className="relative -ml-6 mb-6">
                              <div className="absolute -left-[5px] top-5 h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600 z-[1]" />
                              <div
                                onClick={() => setSelectedExam(exam)}
                                className={`ml-5 flex flex-col bg-white dark:bg-[#273243] rounded-xl border border-slate-200 dark:border-slate-700 p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/70 hover:shadow-md transition-all border-l-4 ${TYPE_BORDER_COLOR[exam.type] || 'border-l-slate-400'}`}
                                data-testid={`exam-card-${exam.id}`}
                              >
                                {/* Header: title + badge top-right */}
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-foreground truncate">{exam.title}</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">{exam.date}, {exam.time}</p>
                                  </div>
                                  {exam.type === 'Referto' && (
                                    <span className="shrink-0 ml-2 rounded px-2 py-0.5 text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300" data-testid={`exam-badge-${exam.id}`}>
                                      Referto
                                    </span>
                                  )}
                                </div>
                                {/* Meta */}
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <Building2 className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate">{exam.facility}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <User className="w-3.5 h-3.5 shrink-0" />
                                    <span>{exam.doctor}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* ═══ RIGHT: Compilazione PAI ═══ */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="flex flex-col h-[750px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden" data-testid="pai-form-container">
                {/* Titolo fisso */}
                <div className="shrink-0 px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-lg font-bold text-foreground dark:text-white" data-testid="pai-title">Compilazione Piano Assistenziale Individualizzato</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Compila i 5 step del wizard per definire obiettivi e interventi</p>
                </div>

                <div className="flex-1 min-h-0 flex flex-col">
                      <div className="flex flex-col flex-1 min-h-0" data-testid="complex-form">

                        {/* ── Stepper orizzontale (fisso in alto) ── */}
                        <div className="shrink-0 px-6 pt-6 pb-2">
                          <div className="flex items-center" data-testid="wizard-stepper">
                          {WIZARD_STEPS.map((step, i) => {
                            const isDone = currentWizardStep > step.num;
                            const isActive = currentWizardStep === step.num;
                            return (
                              <div key={step.num} className="flex items-center flex-1 last:flex-none">
                                <button
                                  onClick={() => setCurrentWizardStep(step.num)}
                                  className="flex flex-col items-center gap-1.5 group cursor-pointer"
                                  data-testid={`stepper-${step.num}`}
                                >
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
                                    ${isDone ? 'bg-primary border-primary text-primary-foreground' : isActive ? 'border-primary bg-primary/10 dark:bg-primary/20 text-primary' : 'border-slate-300 dark:border-slate-600 text-muted-foreground bg-white dark:bg-slate-800'}`}>
                                    {isDone ? <Check className="w-4 h-4" /> : step.num}
                                  </div>
                                  <span className={`text-[10px] font-semibold text-center leading-tight transition-colors ${isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {step.label}
                                  </span>
                                </button>
                                {i < WIZARD_STEPS.length - 1 && (
                                  <div className={`flex-1 h-[2px] mx-2 mt-[-18px] transition-colors ${isDone ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        </div>

                        {/* ── Scrollable step content ── */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                        <AnimatePresence mode="wait">
                          {/* ═══ STEP 1 — Inquadramento ═══ */}
                          {currentWizardStep === 1 && (
                            <motion.div key="step1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.18 }} className="flex flex-col gap-6" data-testid="wizard-step-1">
                              {cmMode && inquadramentoUVMD && (
                                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-xl p-4" data-testid="uvmd-note-step1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Lock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                    <span className="text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider">Note Medico UVMD — Inquadramento</span>
                                  </div>
                                  <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">{inquadramentoUVMD}</p>
                                </div>
                              )}
                              <div className={cardCls}>
                                <div className="flex items-center justify-between mb-5">
                                  <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-0 text-xs font-bold" data-testid="badge-bozza">Stato: Bozza v1.0</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className={labelCls}>Setting Assistenziale</label>
                                    <select value={setting} onChange={e => setSetting(e.target.value)} className={selectCls} data-testid="select-setting">
                                      <option value="">Seleziona setting...</option>
                                      {SETTINGS.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className={labelCls}>Assegna Case Manager</label>
                                    <select value={assignedCM} onChange={e => setAssignedCM(e.target.value)} className={selectCls} data-testid="select-cm">
                                      <option value="">Seleziona CM...</option>
                                      {CM_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className={labelCls}>Inizio PAI</label>
                                    <input type="date" value={dataInizio} onChange={e => setDataInizio(e.target.value)} className={inputCls} data-testid="input-data-inizio" />
                                  </div>
                                  <div>
                                    <label className={labelCls}>Fine Prevista</label>
                                    <input type="date" value={dataFine} onChange={e => setDataFine(e.target.value)} className={inputCls} data-testid="input-data-fine" />
                                  </div>
                                </div>
                                <div>
                                  <label className={labelCls}>Patologia Principale (ICD-9)</label>
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input type="text" value={icd9} onChange={e => setIcd9(e.target.value)} placeholder="Es. 820.0 - Frattura collo del femore" className={`${inputCls} pl-9`} data-testid="input-icd9" />
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* ═══ STEP 2 — Obiettivi ═══ */}
                          {currentWizardStep === 2 && (
                            <motion.div key="step2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.18 }} className="flex flex-col gap-6" data-testid="wizard-step-2">
                              {cmMode && obiettiviUVMD && (
                                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-xl p-4" data-testid="uvmd-note-step2">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Lock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                    <span className="text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider">Note Medico UVMD — Obiettivi</span>
                                  </div>
                                  <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">{obiettiviUVMD}</p>
                                </div>
                              )}
                              <Button variant="outline" size="sm" className="gap-1.5 self-start text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={importCriticita} data-testid="import-criticita-btn">
                                <Download className="w-3.5 h-3.5" /> Importa criticita da valutazione interRAI
                              </Button>
                              {aree.map((a, i) => (
                                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`${cardCls} relative`}>
                                  {aree.length > 1 && (
                                    <button onClick={() => removeArea(i)} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid={`remove-area-${i}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  <div className="mb-3">
                                    <label className={labelCls}>Categoria</label>
                                    <select value={a.categoria} onChange={e => updateArea(i, 'categoria', e.target.value)} className={selectCls} data-testid={`select-cat-${i}`}>
                                      <option value="">Seleziona categoria...</option>
                                      {CATEGORIE_AREA.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className={labelCls}>Obiettivo di Cura</label>
                                    <Textarea value={a.obiettivo} onChange={e => updateArea(i, 'obiettivo', e.target.value)} placeholder="Es. Migliorare la stabilita posturale entro 3 mesi..." rows={3} className="text-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700" data-testid={`textarea-obiettivo-${i}`} />
                                  </div>
                                </motion.div>
                              ))}
                              <Button variant="outline" size="sm" className="gap-1.5 self-start border-dashed border-slate-300 dark:border-slate-600 text-muted-foreground hover:text-foreground" onClick={addArea} data-testid="add-area-btn">
                                <Plus className="w-3.5 h-3.5" /> Aggiungi Area Problematica
                              </Button>
                            </motion.div>
                          )}

                          {/* ═══ STEP 3 — Interventi ═══ */}
                          {currentWizardStep === 3 && (
                            <motion.div key="step3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.18 }} className="flex flex-col gap-6" data-testid="wizard-step-3">
                              {interventi.map((iv, i) => (
                                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`${cardCls} relative`}>
                                  {interventi.length > 1 && (
                                    <button onClick={() => removeIntervento(i)} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid={`remove-intervento-${i}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                      <label className={labelCls}>Figura Professionale</label>
                                      <select value={iv.figura} onChange={e => updateIntervento(i, 'figura', e.target.value)} className={selectCls} data-testid={`select-figura-${i}`}>
                                        <option value="">Seleziona figura...</option>
                                        {FIGURE_PROF.map(f => <option key={f} value={f}>{f}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <label className={labelCls}>Prestazione</label>
                                      <select value={iv.prestazione} onChange={e => updateIntervento(i, 'prestazione', e.target.value)} className={selectCls} data-testid={`select-prestazione-${i}`}>
                                        <option value="">Seleziona prestazione...</option>
                                        {PRESTAZIONI_TIPO.map(p => <option key={p} value={p}>{p}</option>)}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className={labelCls}>Frequenza</label>
                                      <select value={iv.frequenza} onChange={e => updateIntervento(i, 'frequenza', e.target.value)} className={selectCls} data-testid={`select-freq-${i}`}>
                                        <option value="">Seleziona frequenza...</option>
                                        {FREQUENZE.map(f => <option key={f} value={f}>{f}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <label className={labelCls}>Durata (Settimane)</label>
                                      <input type="number" min="1" max="52" value={iv.durata} onChange={e => updateIntervento(i, 'durata', e.target.value)} placeholder="Es. 12" className={inputCls} data-testid={`input-durata-${i}`} />
                                    </div>
                                  </div>
                                </motion.div>
                              ))}

                              {isMediaAlta && (
                                <div className="bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40 p-4 rounded-lg" data-testid="sollievo-section">
                                  <p className="text-xs font-bold text-amber-800 dark:text-amber-300 mb-2">Percorso Sollievo (OSS)</p>
                                  <div className="flex items-center gap-3">
                                    <label className="text-xs text-amber-700 dark:text-amber-400 shrink-0">Monte ore settimanale</label>
                                    <input type="number" min="0" max="40" value={oreSollievo} onChange={e => setOreSollievo(e.target.value)} placeholder="0" className={`${inputCls} w-28`} data-testid="input-ore-sollievo" />
                                    <span className="text-xs text-muted-foreground">ore/sett.</span>
                                  </div>
                                </div>
                              )}

                              <Button variant="outline" size="sm" className="gap-1.5 self-start border-dashed border-slate-300 dark:border-slate-600 text-muted-foreground hover:text-foreground" onClick={addIntervento} data-testid="add-intervento-btn">
                                <Plus className="w-3.5 h-3.5" /> Aggiungi Intervento
                              </Button>
                            </motion.div>
                          )}

                          {/* ═══ STEP 4 — Telemedicina ═══ */}
                          {currentWizardStep === 4 && (
                            <motion.div key="step4" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.18 }} className="flex flex-col gap-6" data-testid="wizard-step-4">
                              <div className={cardCls}>
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-2.5">
                                    <Thermometer className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm font-semibold text-foreground">Attiva Telemonitoraggio</span>
                                  </div>
                                  <button onClick={() => setTeleAttivo(!teleAttivo)} className={`relative w-11 h-6 rounded-full transition-colors ${teleAttivo ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'}`} data-testid="toggle-tele">
                                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${teleAttivo ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                  </button>
                                </div>

                                {teleAttivo && (
                                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
                                    <div className="flex flex-wrap gap-3" data-testid="tele-params">
                                      {TELE_PARAMS.map(tp => {
                                        const Icon = tp.icon;
                                        return (
                                          <label key={tp.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${teleParams[tp.key] ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
                                            <input type="checkbox" checked={teleParams[tp.key]} onChange={() => toggleTeleParam(tp.key)} className="sr-only" />
                                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${teleParams[tp.key] ? 'bg-primary border-primary' : 'border-slate-300 dark:border-slate-600'}`}>
                                              {teleParams[tp.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                                            <span className="text-xs font-medium text-foreground">{tp.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                    {activeTeleParams.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="soglie-grid">
                                        <p className="text-xs font-bold text-foreground mb-3">Soglie di Allerta</p>
                                        <div className="space-y-3">
                                          {activeTeleParams.map(tp => (
                                            <div key={tp.key} className="flex items-center gap-3">
                                              <span className="text-xs font-medium text-muted-foreground w-20 shrink-0">{tp.label}</span>
                                              <div className="flex items-center gap-2 flex-1">
                                                <input type="number" value={soglie[tp.key]?.min ?? tp.defaultMin} onChange={e => updateSoglia(tp.key, 'min', e.target.value)} className={`${inputCls} w-20 text-center`} data-testid={`soglia-min-${tp.key}`} />
                                                <span className="text-xs text-muted-foreground">—</span>
                                                <input type="number" value={soglie[tp.key]?.max ?? tp.defaultMax} onChange={e => updateSoglia(tp.key, 'max', e.target.value)} className={`${inputCls} w-20 text-center`} data-testid={`soglia-max-${tp.key}`} />
                                                <span className="text-[10px] text-muted-foreground">{tp.unit}</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </div>

                              <div>
                                <label className={labelCls}>Fabbisogno Ausili e Presidi</label>
                                <Textarea value={ausili} onChange={e => setAusili(e.target.value)} placeholder="Es. Letto articolato, materasso antidecubito, deambulatore, concentratore O2..." rows={3} className="text-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700" data-testid="textarea-ausili" />
                              </div>
                            </motion.div>
                          )}

                          {/* ═══ STEP 5 — Riepilogo e Firma ═══ */}
                          {currentWizardStep === 5 && (
                            <motion.div key="step5" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.18 }} className="flex flex-col gap-6" data-testid="wizard-step-5">
                              {/* Summary card */}
                              <div className={cardCls} data-testid="riepilogo-card">
                                <p className="text-sm font-bold text-foreground mb-4">Riepilogo Piano Assistenziale</p>
                                <div className="space-y-2.5">
                                  {summaryRows.map(row => (
                                    <div key={row.label} className="flex items-start justify-between gap-4">
                                      <span className="text-xs text-muted-foreground shrink-0">{row.label}</span>
                                      <span className="text-xs font-semibold text-foreground text-right">{row.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Consensi */}
                              <div className={cardCls}>
                                <p className="text-sm font-bold text-foreground mb-4">Consensi Obbligatori</p>
                                <div className="flex flex-col gap-5 mt-4" data-testid="consensi-checkboxes">
                                  {[
                                    { state: consensoInformato, setter: setConsensoInformato, label: 'Consenso informato acquisito', id: 'consenso-informato' },
                                    { state: consensoPrivacy, setter: setConsensoPrivacy, label: 'Consenso trattamento dati (Privacy)', id: 'consenso-privacy' },
                                    { state: presenzaDAT, setter: setPresenzaDAT, label: 'Presenza Direttive Anticipate (DAT)', id: 'presenza-dat' },
                                  ].map(c => (
                                    <label key={c.id} className="flex items-center gap-3 cursor-pointer group" data-testid={c.id}>
                                      <button type="button" onClick={() => c.setter(!c.state)} className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${c.state ? 'bg-primary border-primary' : 'border-slate-300 dark:border-slate-600 group-hover:border-slate-400'}`}>
                                        {c.state && <Check className="w-3 h-3 text-white" />}
                                      </button>
                                      <span className="text-sm text-foreground">{c.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Firma Digitale (inside body) */}
                              <Button size="lg" className="w-full gap-2 h-12 text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md mt-2" disabled={!canSign || isSaving} onClick={() => handleSaveComplex(false)} data-testid="save-complex-btn">
                                {isSaving ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Lock className="w-4 h-4" />}
                                {isSaving ? 'Firma in corso...' : 'Approva e Firma Digitalmente PAI'}
                              </Button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        </div>

                        {/* ── Wizard navigation footer (fisso in basso) ── */}
                        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700" data-testid="wizard-nav">
                          {currentWizardStep > 1 ? (
                            <Button variant="outline" size="lg" className="gap-2 h-11 text-sm" onClick={() => setCurrentWizardStep(s => s - 1)} data-testid="wizard-prev-btn">
                              <ArrowLeft className="w-4 h-4" /> Indietro
                            </Button>
                          ) : <div />}

                          {currentWizardStep < 5 ? (
                            <Button variant="comfort" size="lg" className="gap-2 h-11 text-sm font-bold" onClick={() => setCurrentWizardStep(s => s + 1)} data-testid="wizard-next-btn">
                              Avanti <ArrowRight className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button variant="outline" size="lg" className="gap-2 h-11 text-sm" disabled={isSaving} onClick={() => handleSaveComplex(true)} data-testid="save-draft-btn">
                              <Save className="w-4 h-4" /> Salva in Bozza
                            </Button>
                          )}
                        </div>
                      </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Exam Detail Modal */}
      <AnimatePresence>
        {selectedExam && <ExamDetailModal exam={selectedExam} onClose={() => setSelectedExam(null)} patient={patient} />}
      </AnimatePresence>
    </div>
  );
}
